/**
 * BtnShop Component
 *
 * A comprehensive smart button component that dynamically renders different call-to-action buttons
 * based on user authentication status, course access permissions, completion progress, and academy type.
 * This component serves as the primary navigation and purchase interface for course interactions.
 *
 * Features:
 * - Dynamic button rendering based on multiple conditional states
 * - User authentication and authorization handling
 * - Course progress tracking and completion status monitoring
 * - Integration with external lesson platforms and LTI systems
 * - Purchase flow integration with payment gateway
 * - Multi-language support through i18n
 * - Responsive design with different button styles based on access level
 * - Smart routing with LTI parameter preservation
 * - Support for both free and paid academy models
 *
 * Button States and Scenarios:
 * 1. Authenticated users with course access - Continue course navigation
 * 2. Authenticated users without access - Purchase interface with pricing
 * 3. Unauthenticated users in free academies - Direct course access
 * 4. Unauthenticated users in paid academies - Purchase requirement
 * 5. Users who completed all activities - Completion status message
 * 6. Special handling for external lesson platform integration ('sc' activities)
 *
 * @returns {JSX.Element} Rendered button component with dynamic functionality
 */

import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import Button from '@mui/material/Button';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';

import { getLinkLtik } from 'constants/LTIData';
import classes from './BtnShop.module.css';

/**
 * Extract URL path segments for routing logic and navigation context
 * Used to determine current page structure and build appropriate navigation URLs
 */
const url = window.location.pathname.split('/');

const BtnShop = () => {
  const { t } = useTranslation(['course']);

  // Redux state selectors for accessing global application state
  const dataCourse = useSelector(state => state.courseInfo); // Complete course information and user progress
  const userInfo = useSelector(state => state.userInfo); // User authentication and profile data
  const dataAcademy = useSelector(state => state.academyInfo); // Academy configuration and settings

  // Local state for navigation logic and course progression
  const [nextLesson, setNextLesson] = useState(0); // Next lesson ID for course continuation
  const [nextActivity, setNextActivity] = useState('f'); // Next activity type for user progression

  /**
   * Effect to determine the next lesson and activity based on course structure
   * Analyzes course organization and user progress to calculate appropriate next steps
   * Handles both separated lessons (organized in folders) and flat lesson structure
   */
  useEffect(() => {
    // Handle lessons organized in separators/folders
    if (dataCourse.lessonsCourse.separators) {
      dataCourse.lessonsCourse.separators.map(separator => {
        separator.lessons.map(lesson => {
          setNextLesson(lesson.idVideo);
        });
      });
    }

    // Handle flat lesson structure
    if (dataCourse.lessonsCourse.lessons) {
      dataCourse.lessonsCourse.lessons.map(lesson => {
        setNextLesson(lesson.idVideo);
      });
    }

    // Set next activity from user tracking data
    if (dataCourse.userAditionalTrack.nextTrack) {
      setNextActivity(
        dataCourse.userAditionalTrack.nextTrack.activity
      );
    }
  }, [dataCourse]);

  return (
    <>
      {/* Authenticated user flow */}
      {userInfo.data.nombre ? (
        // User has missing activities (course not completed)
        dataCourse.missingActivities.length > 0 ? (
          <>
            {/* User has access to the course */}
            {dataCourse.accessCourse ? (
              <>
                {/* Handle special case for 'sc' activity type with different URL patterns */}
                {(nextActivity === 'sc' && url[2] === 'course') ||
                (nextActivity === 'sc' && url[3] === 'c') ? (
                  <Button
                    component="a" // HTML anchor element for external navigation
                    target="_blank" // Opens in new tab for external lesson platform
                    href={`https://lesson.mayahii.com/${dataCourse.data.shortNameEmisor}/f/${dataCourse.data.idEmisor}/${dataCourse.data.idLista}/${dataCourse.userAditionalTrack.nextTrack.userTrack}`} // External lesson platform URL with course parameters
                    className={
                      dataCourse.accessCourse
                        ? classes.shopAccessBtn // Styling for users with course access
                        : classes.shopBtn // Default button styling
                    }
                  >
                    {t('continue_course')}{' '}
                    {/* Internationalized "Continue Course" text */}
                  </Button>
                ) : nextActivity === 'sc' ? (
                  <Button
                    component="a" // HTML anchor element for external navigation
                    href={`https://lesson.mayahii.com/${dataCourse.data.shortNameEmisor}/f/${dataCourse.data.idEmisor}/${dataCourse.data.idLista}/${dataCourse.userAditionalTrack.nextTrack.userTrack}`} // External lesson platform URL
                    className={
                      dataCourse.accessCourse
                        ? classes.shopAccessBtn // Styling for users with course access
                        : classes.shopBtn // Default button styling
                    }
                  >
                    {t('continue_course')}{' '}
                    {/* Internationalized "Continue Course" text */}
                  </Button>
                ) : (
                  /* Standard continue course button with dynamic routing */
                  <Button
                    component={Link} // React Router Link component for internal navigation
                    to={
                      getLinkLtik()
                        ? `/${nextActivity}/${
                            dataCourse.data.idLista
                          }/${
                            dataCourse.userAditionalTrack.nextTrack
                              .userTrack
                          }/?ltik=${getLinkLtik()}` // LTI-enabled URL with tracking parameters
                        : url[2] === 'a'
                        ? `/a/${nextActivity}/${dataCourse.data.idLista}/${dataCourse.userAditionalTrack.nextTrack.userTrack}` // Academy-prefixed URL
                        : url[2] === 'course'
                        ? `/course/${nextActivity}/${dataCourse.data.idLista}/${dataCourse.userAditionalTrack.nextTrack.userTrack}` // Course-prefixed URL
                        : `/${nextActivity}/${dataCourse.data.idLista}/${dataCourse.userAditionalTrack.nextTrack.userTrack}` // Standard URL format
                    }
                    className={
                      dataCourse.accessCourse
                        ? classes.shopAccessBtn // Styling for users with course access
                        : classes.shopBtn // Default button styling
                    }
                  >
                    {t('continue_course')}{' '}
                    {/* Internationalized "Continue Course" text */}
                  </Button>
                )}
              </>
            ) : (
              /* User doesn't have access - show purchase option */
              <>
                <p className={classes.specificProductPrice}>
                  {dataCourse.aditionalData.currencySymbol || '$'}{' '}
                  {dataCourse.aditionalData.monto.toFixed(2)}{' '}
                  {dataCourse.aditionalData.currency}
                </p>
                <Button
                  component="a" // HTML anchor element for external payment navigation
                  href={`${process.env.REACT_APP_BASE_URL_PAGOS_MAYAHII}/cart/checkout/academy/course/${dataCourse.data.idEmisor}/${dataCourse.data.shortNameEmisor}/${dataCourse.data.idLista}`} // Payment gateway URL with course parameters
                  className={classes.shopBtn} // Purchase button styling
                >
                  <ShoppingCartIcon /> {t('continue_shopping')}{' '}
                  {/* Shopping cart icon with internationalized purchase text */}
                </Button>
              </>
            )}
          </>
        ) : (
          /* User completed all activities */
          <p className={classes.endCourseText}>
            {t('finished_course')}{' '}
            {/* Internationalized course completion message */}
          </p>
        )
      ) : /* Unauthenticated user flow */
      dataAcademy.data.productType === 1 ? (
        /* Free academy - allow access */
        <Button
          component={Link} // React Router Link component for internal navigation
          to={
            getLinkLtik()
              ? `/f/${
                  dataCourse.data.idLista
                }/${nextLesson}/?ltik=${getLinkLtik()}` // LTI-enabled URL for free course access
              : url[2] === 'a'
              ? `/a/f/${dataCourse.data.idLista}/${nextLesson}` // Academy-prefixed URL for free access
              : url[2] === 'course'
              ? `/course/f/${dataCourse.data.idLista}/${nextLesson}` // Course-prefixed URL for free access
              : `/f/${dataCourse.data.idLista}/${nextLesson}` // Standard URL for free course access
          }
          className={classes.shopBtn} // Standard button styling for free access
        >
          {t('continue_course')}{' '}
          {/* Internationalized "Continue Course" text */}
        </Button>
      ) : (
        /* Paid academy - show purchase option */
        dataCourse.aditionalData && (
          <>
            <p className={classes.specificProductPrice}>
              {dataCourse.aditionalData.currencySymbol || '$'}{' '}
              {dataCourse.aditionalData.monto &&
                dataCourse.aditionalData.monto.toFixed(2)}{' '}
              {dataCourse.aditionalData.currency}
            </p>
            <Button
              component="a" // HTML anchor element for external payment navigation
              href={`${process.env.REACT_APP_BASE_URL_PAGOS_MAYAHII}/cart/checkout/academy/course/${dataCourse.data.idEmisor}/${dataCourse.data.shortNameEmisor}/${dataCourse.data.idLista}`} // Payment gateway URL with course parameters
              className={classes.shopBtn} // Purchase button styling
            >
              <ShoppingCartIcon /> {t('continue_shopping')}{' '}
              {/* Shopping cart icon with internationalized purchase text */}
            </Button>
          </>
        )
      )}
    </>
  );
};

export default BtnShop;
